<template>
  <div>
    <h2 class="content-block">Rows in Review for {{ name }}</h2>

    <h3 class="content-block">Age</h3>

    <dx-data-grid
      ref="review-grid"
      :data-source="summaryDataSource"
      :show-borders="true"
      :remote-operations="false"
      :allow-column-reordering="false"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :word-wrap-enabled="true"
      :column-auto-width="true"
      :show-row-lines="true"
      :show-column-lines="true"
      confirm-delete-message=""
      class="dx-card wide-card"
      width="100%"
    >
      <dx-export
        :enabled="true"
        :file-name="`review-summary-for-${id}-${currentDate}`"
      />
      <dx-filter-row :visible="false" />
      <dx-header-filter :visible="false" />
      <dx-filter-panel :visible="false" />
      <dx-column data-field="bucket" caption="Time bucket" />
      <dx-column
        data-field="telco-telco"
        caption="Telco rows in telco review"
        :format="config.countFormat"
      />
      <dx-column
        data-field="telco-tech"
        caption="Telco rows in tech review"
        :format="config.countFormat"
      />
      <dx-column
        data-field="ledger-telco"
        caption="Ledger rows in telco review"
        :format="config.countFormat"
      />
      <dx-column
        data-field="ledger-tech"
        caption="Ledger rows in tech review"
        :format="config.countFormat"
      />
    </dx-data-grid>

    <h3 class="content-block">Detailed overview</h3>

    <div class="dx-fieldset">
      <DxTextArea
        id="close-review-comment"
        :height="250"
        :value.sync="closeComment"
        value-change-event="keyup"
        placeholder="Comment about closing review..."
      />
      <div class="dx-field">
        <DxButton
          id="close-review"
          :width="180"
          :disabled="!selected.length || !closeComment.length"
          text="Close review"
          @click="closeReview"
        />
      </div>
    </div>

    <dx-data-grid
      ref="review-grid"
      :data-source="dataSource"
      key-expr="id"
      :show-borders="true"
      :remote-operations="false"
      :allow-column-reordering="false"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :word-wrap-enabled="true"
      :column-auto-width="true"
      :show-row-lines="true"
      :show-column-lines="true"
      confirm-delete-message=""
      :selection="{
        mode: 'multiple',
        allowSelectAll: true,
        showCheckBoxesMode: 'always',
      }"
      class="dx-card wide-card"
      width="100%"
      @selection-changed="onSelectionChanged"
    >
      <dx-editing>
        <dx-texts confirm-delete-message="" />
      </dx-editing>
      <dx-column-chooser :enabled="false" />
      <dx-export
        :enabled="true"
        :file-name="`reviews-for-${id}-${currentDate}`"
      />
      <dx-paging :page-size="pageSizes[0]" />
      <dx-pager
        :allowed-page-sizes="pageSizes"
        :show-page-size-selector="true"
        :show-navigation-buttons="true"
        :show-info="true"
      />
      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="false" />
      <dx-filter-panel :visible="true" />
      <dx-column
        data-field="id"
        :format="config.idFormat"
        caption="Row review Id"
      />
      <dx-column
        data-field="rowContent.id"
        :format="config.idFormat"
        caption="Row Id"
      />
      <dx-column
        data-field="rowContent.source"
        :lookup="comparisonSourceTypeColumnLookup"
        caption="Source"
      />
      <dx-column data-field="rowContent.externalIds" caption="External ids" />
      <dx-column
        data-field="rowContent.currencyId"
        :lookup="currencyColumnLookup"
        caption="Currency"
      />
      <dx-column
        data-field="rowContent.amount"
        :format="config.financialNumberFormat"
        caption="Amount"
      />
      <dx-column
        data-field="rowContent.timestamp"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
        caption="Timestamp"
      />
      <dx-column
        data-field="rowContent.rowType"
        :lookup="rowTypeColumnLookup"
        caption="Type"
      />
      <dx-column
        data-field="rowContent.reversal"
        true-text="Yes"
        false-text="No"
        caption="Reversal"
      />
      <dx-column data-field="rowContent.counterParty" caption="Counter party" />
      <dx-column
        data-field="review.created"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
        caption="Review started"
      />
      <dx-column
        data-field="review.type"
        :lookup="reviewTypeColumnLookup"
        caption="Review type"
      />
      <dx-column data-field="review.comment" caption="Review comment" />
    </dx-data-grid>
  </div>
</template>

<script>
	import {mapState} from 'vuex'
	import {
		DxColumn,
		DxColumnChooser,
		DxDataGrid,
		DxEditing,
		DxExport,
		DxFilterPanel,
		DxFilterRow,
		DxHeaderFilter,
		DxPager,
		DxPaging,
		DxTexts,
	} from 'devextreme-vue/data-grid'
	import {DxButton} from 'devextreme-vue/button'
	import {DxTagBox, DxTextArea} from 'devextreme-vue'
	import {formatDate} from 'devextreme/localization'
	import {http} from '@/utils/http'
	import {config} from '@/config'
	import {showHttpError} from '../../utils/http'
	import {show} from '@/utils/show'

	export default {
  name: 'ViewStatement',
  components: {
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxEditing,
    DxExport,
    DxFilterRow,
    DxFilterPanel,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxButton,
    DxTexts,
    // eslint-disable-next-line vue/no-unused-components
    DxTagBox,
    DxTextArea,
  },
  data() {
    return {
	  gateway: this.$route.params.gateway,
      name: null,

      config,

      dataSource: null,
      selected: [],
      pageSizes: config.itemsPerPage,

      summaryDataSource: null,
      buckets: [
        {
          min: 0,
          max: 24,
          text: '<1 day',
        },
        {
          min: 24,
          max: 3 * 24,
          text: '1-3 days',
        },
        {
          min: 3 * 24,
          max: 7 * 24,
          text: '3-7 days',
        },
        {
          min: 7 * 24,
          max: 28 * 24,
          text: '1-4 weeks',
        },
        {
          min: 28 * 24,
          max: 10 * 365 * 24,
          text: '>4 weeks',
        },
      ],

      closeComment: '',
    }
  },
  computed: {
    ...mapState('user', [
      'statementFormatLookup',
      'statementSourceLookup',
      'comparisonSourceLookup',
      'currencyColumnLookup',
      'gatewayLookup',
      'userLookup',
      'rowTypeColumnLookup',
      'reviewTypeColumnLookup',
      'comparisonSourceTypeColumnLookup',
    ]),
    currentDate() {
      return new Date().toISOString().split('T')[0]
    },
  },
  created() {
    this.name = this.gatewayLookup[this.gateway].name
    this.loadData()
  },
  destroyed() {},
  methods: {
    formatLongDate(timeInMillis) {
      return (
        timeInMillis &&
        formatDate(new Date(timeInMillis), this.config.intlDateTimeFormat)
      )
    },
    async loadData() {
      const response = await http
        .get('reconciliation/admin/review/gateway/' + this.gateway)
        .json()
      Object.values(response.rowContents).forEach((row) => {
        row['externalIds'] = row.idAtComparisonSource + ' ' + row.idAtGateway
        row['source'] = row.source
      })
      const rows = response.rowReviews
      rows.forEach((row) => {
        row['review'] = response.reviews[row.reviewId]
        row['rowContent'] = response.rowContents[row.rowContentId]
      })
      this.dataSource = rows

      this.summaryDataSource = this.buckets.map((bucket) => {
        const relevantRows = rows.filter((row) => {
          const ageInHours =
            (response.serverTimeMillis - row.review.created) / (60 * 60 * 1000)
          return bucket.min <= ageInHours && ageInHours < bucket.max
        })
        return {
          bucket: bucket.text,
          'telco-telco': relevantRows.filter(
            (row) =>
              row.review.type === 'TELCO' &&
              row.rowContent.source === 'TELCO',
          ).length,
          'telco-tech': relevantRows.filter(
            (row) =>
              row.review.type === 'INTERNAL_TECH' &&
              row.rowContent.source === 'TELCO',
          ).length,
          'ledger-telco': relevantRows.filter(
            (row) =>
              row.review.type === 'TELCO' &&
              row.rowContent.source === 'LEDGER',
          ).length,
          'ledger-tech': relevantRows.filter(
            (row) =>
              row.review.type === 'INTERNAL_TECH' &&
              row.rowContent.source === 'LEDGER',
          ).length,
        }
      })
    },
    onSelectionChanged(e) {
      this.selected = e.selectedRowsData
    },
    closeReview() {
      const grid = this.$refs['review-grid'].instance
      grid.beginUpdate()
      const selectedIds = this.selected.map((row) => row.id)
      selectedIds.map(grid.getRowIndexByKey).forEach((rowIndex) => {
        grid.deleteRow(rowIndex)
        grid.refresh()
      })
      grid.endUpdate()
      const request = {
        rowReviewIds: selectedIds,
        comment: this.closeComment,
      }
      this.selected = []
      this.closeComment = ''

      http
        .post('reconciliation/admin/review/close', {
          json: request,
        })
        .then(() => {
          show.success('Review closed')
        })
        .catch((e) => {
          showHttpError(e)
        })
    },
  },
}
</script>
<style lang="scss">
td[role='columnheader'] {
  text-align: center !important;
}

.dx-button {
  float: left !important;
}

#close-review {
  margin-top: 10px;
}

#close-review-comment {
  border: 1px solid gray;
  padding: 5px;
}
</style>
